import React, { useState } from "react";
import Icon from "../../../components/AppIcon";

const Footer = () => {
  const [activeModal, setActiveModal] = useState(null);
  const currentYear = new Date()?.getFullYear();

  const footerSections = [
    {
      title: "App",
      links: [
        { label: "Features", href: "benefits", type: "scroll" },
        { label: "Screenshots", href: "screenshots", type: "scroll" },
        { label: "Download", href: "app-download", type: "scroll" },
        { label: "System Requirements", href: "requirements", type: "modal" },
      ],
    },
    {
      title: "Support",
      links: [
        { label: "Help Center", href: "help", type: "modal" },
        { label: "Contact Support", href: "contact", type: "email" },
        { label: "Report a Bug", href: "bug-report", type: "email" },
        { label: "FAQ", href: "faq", type: "scroll" },
      ],
    },
    {
      title: "Company",
      links: [
        { label: "About Us", href: "about", type: "modal" },
        { label: "Privacy Policy", href: "privacy", type: "modal" },
        { label: "Terms of Service", href: "terms", type: "modal" },
        { label: "Contact Us", href: "contact", type: "email" },
      ],
    },
  ];

  const socialLinks = [
    {
      name: "Facebook",
      icon: "Facebook",
      href: "https://www.facebook.com/share/17WkQTwpwR/",
    },
    { name: "X", icon: "X", href: "#x" },
    {
      name: "Instagram",
      icon: "Instagram",
      href: "https://www.instagram.com/tak_business_manager",
    },
    { name: "LinkedIn", icon: "Linkedin", href: "#linkedin" },
  ];

  const trustBadges = [
    { name: "SSL Secured", icon: "Lock", description: "Bank-level Security" },
    {
      name: "Privacy Protected",
      icon: "Shield",
      description: "GDPR Compliant",
    },
    { name: "99.9% Uptime", icon: "Zap", description: "Always Available" },
    {
      name: "24/7 Support",
      icon: "Headphones",
      description: "We're Here to Help",
    },
  ];

  const handleLinkClick = (linkData) => {
    // Handle string hrefs (legacy)
    if (typeof linkData === "string") {
      const href = linkData;
      // Check if it's an external URL
      if (href?.startsWith("http://") || href?.startsWith("https://")) {
        window.open(href, "_blank", "noopener,noreferrer");
        return;
      }
      // Handle internal anchors
      if (href?.startsWith("#")) {
        const element = document.getElementById(href?.substring(1));
        if (element) {
          element?.scrollIntoView({ behavior: "smooth" });
        }
      } else {
        const element = document.getElementById(href);
        if (element) {
          element?.scrollIntoView({ behavior: "smooth" });
        }
      }
      return;
    }

    // Handle new link objects with types
    const { href, type } = linkData;
    switch (type) {
      case "scroll":
        const element = document.getElementById(href);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
        break;
      case "modal":
        setActiveModal(href);
        break;
      case "email":
        if (href === "contact" || href === "bug-report") {
          window.location.href =
            "mailto:support@takbusiness.com?subject=" +
            (href === "bug-report" ? "Bug Report" : "Support Request");
        }
        break;
      default:
        break;
    }
  };

  const modalContent = {
    requirements: {
      title: "System Requirements",
      content: (
        <div className="space-y-4">
          <div>
            <h4 className="font-semibold mb-2">Android</h4>
            <ul className="text-gray-600 space-y-1">
              <li>• Android 6.0 (API level 23) or higher</li>
              <li>• 100 MB available storage</li>
              <li>• Internet connection required</li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">iOS (Coming Soon)</h4>
            <ul className="text-gray-600 space-y-1">
              <li>• iOS 12.0 or higher</li>
              <li>• iPhone 6s or newer</li>
              <li>• 100 MB available storage</li>
              <li>• Internet connection required</li>
            </ul>
          </div>
        </div>
      ),
    },
    help: {
      title: "Help Center",
      content: (
        <div className="space-y-4">
          <p className="text-gray-600">
            Welcome to the TAK Business Manager Help Center!
          </p>
          <div className="space-y-3">
            <div>
              <h4 className="font-semibold mb-1">Getting Started</h4>
              <p className="text-gray-600 text-sm">
                Learn how to set up your business profile and start tracking
                sales.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-1">Managing Inventory</h4>
              <p className="text-gray-600 text-sm">
                Add, edit, and track your products with ease.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-1">Sales Reports</h4>
              <p className="text-gray-600 text-sm">
                Generate detailed reports to understand your business
                performance.
              </p>
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-4">
            For detailed guides, please contact our support team at
            support@takbusiness.com
          </p>
        </div>
      ),
    },
    about: {
      title: "About Us",
      content: (
        <div className="space-y-4">
          <p className="text-gray-600">
            TAK Business Manager is designed to help small and medium businesses
            manage their operations efficiently through mobile technology.
          </p>
          <p className="text-gray-600">
            Our mission is to provide powerful, easy-to-use business management
            tools that help entrepreneurs focus on growing their business rather
            than managing paperwork.
          </p>
          <div className="border-t pt-4 mt-4">
            <h4 className="font-semibold mb-2">Contact Information</h4>
            <p className="text-gray-600 text-sm">Email: info@takbusiness.com</p>
            <p className="text-gray-600 text-sm">
              Support: support@takbusiness.com
            </p>
          </div>
        </div>
      ),
    },
    privacy: {
      title: "Privacy Policy",
      content: (
        <div className="space-y-4">
          <p className="text-gray-600">
            Your privacy is important to us. This Privacy Policy explains how we
            collect, use, and protect your information when you use TAK Business
            Manager.
          </p>
          <div>
            <h4 className="font-semibold mb-2">Information We Collect</h4>
            <ul className="text-gray-600 space-y-1 text-sm">
              <li>• Business information you provide</li>
              <li>• Sales and inventory data</li>
              <li>• App usage analytics</li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">How We Use Your Information</h4>
            <ul className="text-gray-600 space-y-1 text-sm">
              <li>• To provide and improve our services</li>
              <li>• To generate your business reports</li>
              <li>• To provide customer support</li>
            </ul>
          </div>
          <p className="text-sm text-gray-500">
            Last updated: {new Date().toLocaleDateString()}
          </p>
        </div>
      ),
    },
    terms: {
      title: "Terms of Service",
      content: (
        <div className="space-y-4">
          <p className="text-gray-600">
            By using TAK Business Manager, you agree to these Terms of Service.
          </p>
          <div>
            <h4 className="font-semibold mb-2">Acceptable Use</h4>
            <ul className="text-gray-600 space-y-1 text-sm">
              <li>• Use the app for legitimate business purposes</li>
              <li>• Keep your account information secure</li>
              <li>• Do not share your account credentials</li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">Service Availability</h4>
            <p className="text-gray-600 text-sm">
              We strive to provide reliable service but cannot guarantee 100%
              uptime.
            </p>
          </div>
          <div>
            <h4 className="font-semibold mb-2">Data Backup</h4>
            <p className="text-gray-600 text-sm">
              We recommend regularly backing up your important business data.
            </p>
          </div>
          <p className="text-sm text-gray-500">
            Last updated: {new Date().toLocaleDateString()}
          </p>
        </div>
      ),
    },
  };

  const Modal = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-md w-full max-h-[80vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 text-xl"
              >
                ×
              </button>
            </div>
            <div className="text-gray-900">{children}</div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Modal */}
      <Modal
        isOpen={!!activeModal}
        onClose={() => setActiveModal(null)}
        title={activeModal ? modalContent[activeModal]?.title : ""}
      >
        {activeModal ? modalContent[activeModal]?.content : ""}
      </Modal>

      <footer className="bg-gray-900 text-white">
        {/* Main Footer Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid lg:grid-cols-5 gap-8">
            {/* Company Info */}
            <div className="lg:col-span-2">
              <div className="flex items-center space-x-2 mb-6">
                <img
                  src="/assets/logos/App_Icon.png"
                  alt="TAK Business Manager"
                  className="w-10 h-10 rounded-lg"
                />
                <span className="text-xl font-bold">TAK Business Manager</span>
              </div>

              <p className="text-gray-400 mb-6 leading-relaxed">
                Manage your business on the go with TAK Business Manager mobile
                app. Track sales, manage inventory, and monitor performance -
                all from your smartphone.
              </p>

              {/* App Download Badges */}
              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <div className="inline-flex items-center justify-center px-4 py-2 bg-gray-600 text-white rounded-lg opacity-60 cursor-not-allowed">
                  <Icon name="Apple" size={20} className="mr-3" />
                  <div className="text-left">
                    <div className="text-xs">Coming Soon</div>
                    <div className="text-sm font-semibold">App Store</div>
                  </div>
                </div>
                <a
                  href="https://play.google.com/store/apps/details?id=com.tak.business_pos"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
                >
                  <Icon name="Smartphone" size={20} className="mr-3" />
                  <div className="text-left">
                    <div className="text-xs">Get it on</div>
                    <div className="text-sm font-semibold">Google Play</div>
                  </div>
                </a>
              </div>

              {/* Contact Info */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center text-gray-400">
                  <Icon name="Mail" size={16} className="mr-3" />
                  <span className="text-sm">support@takbusiness.com</span>
                </div>
                <div className="flex items-center text-gray-400">
                  <Icon name="Phone" size={16} className="mr-3" />
                  <span className="text-sm">+254 111462289</span>
                </div>
              </div>

              {/* Social Links */}
              <div className="flex space-x-4">
                {socialLinks?.map((social) => (
                  <button
                    key={social?.name}
                    onClick={() => handleLinkClick(social?.href)}
                    className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-primary transition-colors"
                    aria-label={social?.name}
                  >
                    <Icon name={social?.icon} size={18} />
                  </button>
                ))}
              </div>
            </div>

            {/* Footer Links */}
            {footerSections?.map((section) => (
              <div key={section?.title}>
                <h3 className="font-semibold text-white mb-4">
                  {section?.title}
                </h3>
                <ul className="space-y-3">
                  {section?.links?.map((link) => (
                    <li key={link?.label}>
                      <button
                        onClick={() => handleLinkClick(link)}
                        className="text-gray-400 hover:text-white transition-colors text-sm"
                      >
                        {link?.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Trust Badges */}
          <div className="border-t border-gray-800 mt-12 pt-8">
            <div className="grid md:grid-cols-4 gap-6">
              {trustBadges?.map((badge) => (
                <div key={badge?.name} className="text-center">
                  <div className="w-12 h-12 bg-gray-800 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <Icon
                      name={badge?.icon}
                      size={20}
                      className="text-primary"
                    />
                  </div>
                  <div className="font-medium text-white text-sm mb-1">
                    {badge?.name}
                  </div>
                  <div className="text-gray-400 text-xs">
                    {badge?.description}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* Bottom Bar */}
        <div className="border-t border-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="text-gray-400 text-sm mb-4 md:mb-0">
                <div>
                  © {currentYear} TAK Business Manager. All rights reserved.
                </div>
                <div className="text-xs mt-1">
                  A product of TAK Kinship Technologies
                </div>
              </div>

              <div className="flex flex-wrap gap-6 text-sm">
                <button
                  onClick={() =>
                    handleLinkClick({ href: "privacy", type: "modal" })
                  }
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  Privacy Policy
                </button>
                <button
                  onClick={() =>
                    handleLinkClick({ href: "terms", type: "modal" })
                  }
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  Terms of Service
                </button>
                <button
                  onClick={() =>
                    handleLinkClick({ href: "contact", type: "email" })
                  }
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  Contact
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* Back to Top Button */}
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-8 right-8 w-12 h-12 bg-primary rounded-full flex items-center justify-center shadow-lg hover:bg-primary/90 transition-colors z-40 lg:hidden"
          aria-label="Back to top"
        >
          <Icon name="ChevronUp" size={20} className="text-white" />
        </button>
      </footer>
    </>
  );
};

export default Footer;
